/**
 * An event's feed: one of its org's sources, picked by name and status
 * instead of typed in as a raw MoQ namespace (JSS#1159).
 *
 * What is stored is unchanged: the event's `moqNamespace`. For a `moq`
 * source the namespace IS the source id: channel-service hands a device
 * `moqNamespace = sourceId` (device-sources.ts attachConnectionDetails), and
 * event-manager resolves a namespace to its owner the same way, as the id of
 * a `type = 'moq'` source (db-common resolveStreamOwnership). Only `moq`
 * sources are listed: a CMAF or RIFT source's id is not a namespace the
 * viewer page can subscribe to.
 *
 * event-manager refuses a namespace that is another org's stream (404
 * `Stream not found`, checkProgrammeStream), and accepts any other string. So:
 *
 *   - Only the event's org's sources are offered. The edit form and the
 *     Config view pass the event's `org_id` for everyone. For an org user
 *     that filter changes nothing, because their GET /api/sources is already
 *     their acting org's, and an event they can open is that org's. In the
 *     all-orgs view a superuser's list spans every org, so the filter is
 *     what narrows it. The create dialog passes the owner pick: undefined
 *     for an org user (their list is already scoped), the chosen org for a
 *     superuser.
 *   - A value no listed source has (another org's, deleted, a channel, or
 *     typed in) is kept and shown as `Custom: <value>`, never cleared. While
 *     the picker is open it stays selectable as `Current: <value>`, so a
 *     mistaken pick can be undone without cancelling the edit.
 *   - `Custom namespace…` keeps the old free-text input for anyone who can
 *     edit the event. A source list can be empty (device sources had no org
 *     until JSS#1165), and channel ids and namespaces that are not registered
 *     yet are valid feeds. The server checks every namespace on POST and PATCH
 *     (checkProgrammeStream), so this is not a privilege boundary. It sits
 *     after the sources as the secondary choice.
 *   - A `retired` source (JSS#1151: a device source no AirChain on its site
 *     references any more) is never offered: the relay refuses publishes to
 *     it (db-common authorizeMoqPublish), so it is not a feed to pick. An
 *     event already pointing at one keeps it, and it is still named (with its
 *     `retired` status) in the trigger and the Config view.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Check, Radio } from 'lucide-react';
import { sourcesApi } from '@/api/client';
import { cn } from '@/lib/utils';
import {
  originPath,
  originTitle,
  useProductionIndex,
  type ProductionIndex,
  type SourceOrigin,
} from '@/hooks/useProductionIndex';
import type { Source } from '@/types';

/**
 * The sources an event can take its feed from: the org's `moq` sources,
 * active first, never a retired one. `known` adds the retired ones, only to
 * name a value an event already stores.
 */
export function useFeedSources(orgId: string | undefined) {
  const query = useQuery({
    queryKey: ['sources'],
    queryFn: () => sourcesApi.list(),
    staleTime: 30_000,
  });
  const sources = useMemo(
    () => feedSources(query.data ?? [], orgId),
    [query.data, orgId],
  );
  const known = useMemo(
    () => feedSources(query.data ?? [], orgId, { includeRetired: true }),
    [query.data, orgId],
  );
  return { sources, known, isLoading: query.isLoading, isError: query.isError };
}

const STATUS_ORDER: Record<string, number> = { active: 0, idle: 1, error: 2 };

export function feedSources(
  all: readonly Source[],
  orgId: string | undefined,
  { includeRetired = false }: { includeRetired?: boolean } = {},
): Source[] {
  return all
    .filter((s) => s.type === 'moq')
    .filter((s) => includeRetired || s.status !== 'retired')
    .filter((s) => orgId === undefined || s.orgId === orgId)
    .sort((a, b) => {
      const byStatus = (STATUS_ORDER[a.status] ?? 3) - (STATUS_ORDER[b.status] ?? 3);
      if (byStatus !== 0) return byStatus;
      return (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
    });
}

/** The namespace an event stores for a source. */
export function namespaceOf(source: Source): string {
  return source.id;
}

/**
 * What tells two same-named sources apart ("jbs-device - moq-egress" is most
 * of them): where it came from, when it was created, and its short id.
 */
export function sourceDisambiguator(source: Source): string {
  const parts: string[] = [];
  if (source.deviceId) parts.push('JBS device');
  if (source.createdAt) {
    const d = new Date(source.createdAt);
    if (!Number.isNaN(d.getTime())) parts.push(`created ${d.toLocaleDateString()}`);
  }
  parts.push(source.id.slice(0, 8));
  return parts.join(' · ');
}

// ---- Production origins (JSS#1161 step 2) ----
// A source a JBS production's cloud output publishes to is labelled by that
// production and AirChain instead of its generic device-registered name, and
// the picker groups such sources by production, live ones first. A source no
// production claims (another kind of source, or its site's read failed)
// renders exactly as before.

/** The option's primary label: "Production › AirChain", else the source name. */
export function feedTitle(source: Source, origin: SourceOrigin | undefined): string {
  return origin ? originTitle(origin) : source.name;
}

/** The secondary line for a production-published source: output, site, short id. */
function originDisambiguator(source: Source, origin: SourceOrigin): string {
  return [origin.output, origin.siteName, source.id.slice(0, 8)].join(' · ');
}

export interface FeedGroup {
  /** Stable key; '' for the trailing group of sources no production claims. */
  key: string;
  /** "NAB 2026 · Studio A"; absent for the unclaimed group. */
  heading?: string;
  onAir: boolean;
  sources: Source[];
}

/**
 * Groups the picker's sources: one group per production (an AirChain bound
 * to no production gets its site's "Not in a production" group), on-air
 * groups first and on-air sources first within a group, then the sources no
 * production claims in the order given.
 */
export function groupFeedSources(sources: readonly Source[], index: ProductionIndex): FeedGroup[] {
  const groups = new Map<string, FeedGroup>();
  const rest: Source[] = [];
  for (const s of sources) {
    const o = index.get(s.id);
    if (!o) {
      rest.push(s);
      continue;
    }
    const key = `${o.siteId}/${o.production?.name ?? ''}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        key,
        heading: `${o.production?.label ?? 'Not in a production'} · ${o.siteName}`,
        onAir: false,
        sources: [],
      };
      groups.set(key, g);
    }
    g.sources.push(s);
    if (o.onAir) g.onAir = true;
  }
  const onAir = (s: Source) => (index.get(s.id)?.onAir ? 0 : 1);
  const ordered = [...groups.values()]
    .map((g) => ({ ...g, sources: [...g.sources].sort((a, b) => onAir(a) - onAir(b)) }))
    .sort((a, b) => Number(b.onAir) - Number(a.onAir) || (a.heading ?? '').localeCompare(b.heading ?? ''));
  if (rest.length > 0) ordered.push({ key: '', onAir: false, sources: rest });
  return ordered;
}

function OnAirBadge() {
  return (
    <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-red-400">
      <Radio size={10} /> On air
    </span>
  );
}

function StatusDot({ status }: { status: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium',
        status === 'active'
          ? 'bg-jeket-healthy/20 text-jeket-healthy'
          : status === 'error'
            ? 'bg-red-500/20 text-red-400'
            : 'bg-jeket-silver-muted/20 text-jeket-silver-muted',
      )}
    >
      {status}
    </span>
  );
}

const CUSTOM = '__custom__';
const NONE = '__none__';

export interface FeedPickerProps {
  id?: string;
  /** The stored `moqNamespace`; '' for no feed. */
  value: string;
  onChange: (namespace: string) => void;
  /**
   * The event's org, when known: only its sources are offered. Undefined for
   * an org user, whose source list is already their org's.
   */
  orgId?: string;
  /** A superuser has not picked the owner org yet: nothing to list. */
  orgPending?: boolean;
  /** Classes for the trigger and the custom input, from the form it sits in. */
  inputClassName: string;
}

export function FeedPicker({ id, value, onChange, orgId, orgPending, inputClassName }: FeedPickerProps) {
  const { sources, known, isLoading, isError } = useFeedSources(orgId);
  const { index: origins } = useProductionIndex();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [customMode, setCustomMode] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  // The value the form opened with: offered back as `Current:` when no
  // listed source has it.
  const [original] = useState(() => value.trim());

  const trimmed = value.trim();
  // A retired source is not offered, but one the event already has is still
  // named rather than shown as Custom.
  const selected = trimmed ? known.find((s) => namespaceOf(s) === trimmed) : undefined;
  // Unknown only once the list has answered: a value is never "custom" just
  // because the sources are still loading.
  const isCustomValue = trimmed !== '' && !selected && !isLoading;
  const showCustomInput = customMode || (isError && trimmed !== '');
  const showCurrent =
    original !== '' && !isLoading && !sources.some((s) => namespaceOf(s) === original);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const q = search.trim().toLowerCase();
  const filtered = q
    ? sources.filter((s) => {
        const o = origins.get(s.id);
        const fields = [s.name, s.id, s.status, s.type];
        if (o) fields.push(originPath(o), o.siteName);
        return fields.some((f) => (f ?? '').toLowerCase().includes(q));
      })
    : sources;
  const groups = groupFeedSources(filtered, origins);

  function pick(key: string) {
    setOpen(false);
    setSearch('');
    if (key === CUSTOM) {
      setCustomMode(true);
      return;
    }
    setCustomMode(false);
    onChange(key === NONE ? '' : key);
  }

  function pickCurrent() {
    setOpen(false);
    setSearch('');
    setCustomMode(false);
    onChange(original);
  }

  let triggerLabel: ReactNode;
  if (orgPending) {
    triggerLabel = <span className="text-jeket-silver-muted/70">Choose the organization first</span>;
  } else if (trimmed === '') {
    triggerLabel = <span className="text-jeket-silver-muted">No feed</span>;
  } else if (selected) {
    const o = origins.get(selected.id);
    triggerLabel = (
      <span className="flex items-center gap-2 min-w-0">
        <span className="truncate">{feedTitle(selected, o)}</span>
        {o?.onAir && <OnAirBadge />}
        <StatusDot status={selected.status} />
        <span className="text-[11px] text-jeket-silver-muted truncate">
          {o ? originDisambiguator(selected, o) : sourceDisambiguator(selected)}
        </span>
      </span>
    );
  } else if (isCustomValue || isError) {
    triggerLabel = <span className="font-mono truncate">Custom: {trimmed}</span>;
  } else {
    triggerLabel = <span className="font-mono truncate">{trimmed}</span>;
  }

  const listboxId = `${id ?? 'feed-picker'}-listbox`;

  return (
    <div ref={rootRef} className="relative">
      <button
        id={id}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        disabled={orgPending}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOpen(false);
        }}
        className={cn(inputClassName, 'flex items-center justify-between gap-2 text-left disabled:opacity-60')}
      >
        {triggerLabel}
        <ChevronDown size={14} className="shrink-0 text-jeket-silver-muted" />
      </button>

      {open && (
        <div className="absolute z-20 mt-1 w-full bg-jeket-black-surface border border-jeket-gold/20 rounded-lg shadow-jeket p-2">
          <input
            type="text"
            autoFocus
            aria-label="Search feeds"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpen(false);
            }}
            placeholder="Search sources"
            className="w-full mb-2 bg-jeket-black-elevated border border-jeket-gold/20 rounded-md px-2 py-1.5 text-xs text-jeket-silver-bright placeholder:text-jeket-silver-muted/50 focus:outline-none focus:border-jeket-gold/50"
          />
          <ul id={listboxId} role="listbox" aria-label="Feeds" className="max-h-64 overflow-y-auto space-y-0.5">
            <Option selected={trimmed === ''} onSelect={() => pick(NONE)}>
              <span className="text-jeket-silver-muted">No feed</span>
            </Option>
            {isLoading && <li className="px-2 py-1.5 text-xs text-jeket-silver-muted">Loading sources…</li>}
            {isError && <li className="px-2 py-1.5 text-xs text-jeket-silver-muted">Sources could not be loaded.</li>}
            {!isLoading && !isError && filtered.length === 0 && (
              <li className="px-2 py-1.5 text-xs text-jeket-silver-muted">
                {q ? 'No source matches.' : 'This organization has no MoQ sources.'}
              </li>
            )}
            {groups.map((g) => {
              const options = g.sources.map((s) => {
                const o = origins.get(s.id);
                return (
                  <Option key={s.id} selected={selected?.id === s.id} onSelect={() => pick(namespaceOf(s))}>
                    <span className="flex flex-col min-w-0">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="truncate text-jeket-silver-bright">{feedTitle(s, o)}</span>
                        <span className="text-[10px] uppercase text-jeket-silver-muted">{s.type}</span>
                        {o?.onAir && <OnAirBadge />}
                        <StatusDot status={s.status} />
                      </span>
                      <span className="text-[11px] text-jeket-silver-muted truncate">
                        {o ? originDisambiguator(s, o) : sourceDisambiguator(s)}
                      </span>
                    </span>
                  </Option>
                );
              });
              if (!g.heading) return options;
              return (
                <li key={g.key} role="group" aria-label={g.heading}>
                  <div
                    role="presentation"
                    className="px-2 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-jeket-silver-muted"
                  >
                    {g.heading}
                  </div>
                  <ul role="none" className="space-y-0.5">
                    {options}
                  </ul>
                </li>
              );
            })}
            {showCurrent && (
              <Option selected={trimmed === original && !customMode} onSelect={() => pickCurrent()}>
                <span className="font-mono text-xs text-jeket-silver-bright break-all">Current: {original}</span>
              </Option>
            )}
            <li role="separator" className="my-1 border-t border-jeket-black-elevated" />
            <Option selected={false} onSelect={() => pick(CUSTOM)}>
              <span className="flex flex-col">
                <span className="text-jeket-silver-muted">Custom namespace…</span>
                <span className="text-[11px] text-jeket-silver-muted/70">
                  For a channel, or a namespace no source lists yet (advanced)
                </span>
              </span>
            </Option>
          </ul>
        </div>
      )}

      {showCustomInput && (
        <input
          type="text"
          aria-label="Custom namespace"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="e.g. bcbe2908-7b84-4eaf-a507-e9cd5d03fa88"
          spellCheck={false}
          className={cn(inputClassName, 'mt-2 font-mono')}
        />
      )}
    </div>
  );
}

function Option({
  selected,
  onSelect,
  children,
}: {
  selected: boolean;
  onSelect: () => void;
  children: ReactNode;
}) {
  return (
    <li
      role="option"
      aria-selected={selected}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      className={cn(
        'flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-sm cursor-pointer hover:bg-jeket-black-elevated focus:outline-none focus:bg-jeket-black-elevated',
        selected && 'bg-jeket-black-elevated',
      )}
    >
      {children}
      {selected && <Check size={12} className="shrink-0 text-jeket-gold" />}
    </li>
  );
}

/**
 * An event's feed in the read-only Config view: the source's name and status
 * when the namespace is one of the event's org's sources, else the namespace.
 */
export function FeedLabel({ namespace, orgId }: { namespace: string | null; orgId?: string }) {
  const { known: sources } = useFeedSources(orgId);
  const { index: origins } = useProductionIndex();
  const ns = (namespace ?? '').trim();
  if (ns === '') {
    return <span className="text-jeket-silver-muted/60">Not set (using build-time fallback)</span>;
  }
  const source = sources.find((s) => namespaceOf(s) === ns);
  if (!source) return <span className="font-mono break-all">{ns}</span>;
  const origin = origins.get(source.id);
  if (origin) {
    return (
      <span className="inline-flex flex-wrap items-center gap-2" data-testid="feed-origin">
        <span>{originPath(origin)}</span>
        {origin.onAir && <OnAirBadge />}
        <StatusDot status={source.status} />
        <span className="text-[11px] text-jeket-silver-muted font-mono break-all" title={ns}>
          {origin.siteName} · {source.id.slice(0, 8)}
        </span>
      </span>
    );
  }
  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      <span>{source.name}</span>
      <StatusDot status={source.status} />
      <span className="text-[11px] text-jeket-silver-muted font-mono break-all" title={ns}>
        {sourceDisambiguator(source)}
      </span>
    </span>
  );
}
